/**
 * Overlapping avatar group. Used on every list page that needs to
 * show multi-assignee tasks at a glance — Space List view's
 * `AssigneeCell` has its own picker-aware version; this one is the
 * read-only sibling for Team Tasks, Task Monitor, etc. Every person is
 * drawn as its own avatar — there is no "+N" overflow badge.
 *
 * Accepts a `people` prop in one of two shapes (so callers can pass
 * whatever their query already returns without re-mapping):
 *
 *   - `[{ name }]`  — plain list of names (Task Monitor's
 *     `assignees: [{ id, name }]` shape works directly)
 *   - `[{ fullName }]` — Workspace-member shape (Team Tasks)
 *
 * Both fields are checked so a single component covers both data
 * sources without exploding into per-page variants.
 *
 * `size` defaults to 24 (matches what Team Tasks used previously).
 */

import { Avatar } from './Avatar'

// Map a pixel size to the Avatar component's nearest tier. The
// shared Avatar component takes string tiers (`xs`/`sm`/...); the
// AvatarStack API stays numeric so callers can mix pixel sizes for
// the overflow badge. Returns the tier whose pixel size is closest
// to the requested value.
const TIER_PX = [
  ['xs', 24],
  ['sm', 28],
  ['md', 36],
  ['lg', 48],
  ['xl', 64],
  ['2xl', 80],
]
function nearestTier(px) {
  let best = TIER_PX[0]
  let bestDelta = Math.abs(best[1] - px)
  for (const tier of TIER_PX) {
    const delta = Math.abs(tier[1] - px)
    if (delta < bestDelta) {
      best = tier
      bestDelta = delta
    }
  }
  return best[0]
}

export function AvatarStack({ people, size = 24 }) {
  const list = Array.isArray(people) ? people : []
  const tier = nearestTier(size)
  if (list.length === 0) return <Avatar name={null} size={tier} />
  // Every person is drawn as its own avatar — no "+N" overflow chip.
  return (
    <div className="inline-flex items-center -space-x-2">
      {list.map((m, i) => {
        const personName = m.fullName ?? m.name ?? null
        return (
          <span
            key={m.userId ?? m.id ?? personName ?? i}
            className="ring-2 ring-[color:var(--color-card)] rounded-full"
            style={{ zIndex: list.length - i }}
          >
            <Avatar
              name={personName}
              avatarUrl={m.avatarUrl ?? null}
              size={tier}
              // Per-name hash colour + styled hover tooltip so the
              // stack matches the Space List view — varied colours
              // per assignee, names appear in a chip on hover.
              colorByName
              tooltip
            />
          </span>
        )
      })}
    </div>
  )
}
